import { Loader2Icon } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceDetailField } from '@/products/maintenance/features/maintenance-detail-field';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import {
  maintenanceProjectInstallationsPageFromSearch,
  maintenanceProjectInstallationsPath,
} from '@/products/maintenance/features/maintenance-project-installations-path';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import {
  useMaintenanceInstallations,
  useMaintenanceProject,
} from '@/products/maintenance/features/use-maintenance';
import {
  maintenanceCustomerPath,
  maintenanceInstallationPath,
  maintenanceProjectEditPath,
  maintenanceProjectInstallationNewPath,
} from '@/products/maintenance/maintenance-paths';

export function MaintenanceProjectDetailPage() {
  const { projectId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const installationPage = maintenanceProjectInstallationsPageFromSearch(search.toString());
  const project = useMaintenanceProject(projectId);
  const installations = useMaintenanceInstallations({
    page: installationPage,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    projectId,
  });

  usePageTitle(project.data?.name ?? 'Projekt');

  if (project.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Projekt wird geladen …
      </p>
    );
  }
  if (project.isError || !project.data) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Das Projekt wurde nicht gefunden.
      </p>
    );
  }

  const row = project.data;
  const siteLines = [
    row.siteAddress.line1,
    row.siteAddress.line2,
    `${row.siteAddress.postalCode} ${row.siteAddress.city}`,
    row.siteAddress.country,
  ]
    .filter((line) => line && String(line).trim().length > 0)
    .join('\n');
  const installItems = installations.data?.items ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm">
        <Link className="text-primary hover:underline" to={maintenanceCustomerPath(row.customerId)}>
          ← Zum Kunden
        </Link>
      </p>

      <PlatformPageHeader
        title={row.name}
        description={`Kunde: ${row.customer.name}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="min-h-11" asChild>
              <Link to={maintenanceProjectEditPath(row.id)}>Bearbeiten</Link>
            </Button>
            <Button className="min-h-11" disabled={!row.isActive} asChild={row.isActive}>
              {row.isActive ? (
                <Link to={maintenanceProjectInstallationNewPath(row.id)}>Anlage anlegen</Link>
              ) : (
                <span>Anlage anlegen</span>
              )}
            </Button>
          </div>
        }
      />

      {!row.isActive ? (
        <p className="text-sm text-muted-foreground">
          Inaktive Projekte erhalten keine neuen Anlagen.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Stammdaten
            <MaintenanceStatusBadge status={row.isActive} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <MaintenanceDetailField label="Projektnummer" value={row.projectNumber ?? '—'} />
          <MaintenanceDetailField label="Name" value={row.name} />
          <MaintenanceDetailField label="Beschreibung" value={row.description ?? '—'} />
          <div className="min-w-0 space-y-1 sm:col-span-2">
            <p className="text-xs text-muted-foreground">Standortadresse</p>
            <p className="text-sm font-medium break-words whitespace-pre-line">{siteLines}</p>
          </div>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Anlagen</h2>
        {installations.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Anlagen werden geladen …
          </p>
        ) : null}
        {installations.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Anlagen in diesem Projekt.</p>
        ) : null}
        {installItems.length > 0 && installations.data ? (
          <>
            <MaintenanceRecordList
              rows={installItems}
              getRowId={(installation) => installation.id}
              getHref={(installation) => maintenanceInstallationPath(installation.id)}
              getTitle={(installation) => installation.name}
              getMeta={(installation) => installation.installationNumber}
              getStatus={(installation) => installation.isActive}
              titleHeader="Bezeichnung"
              columns={[
                {
                  id: 'number',
                  header: 'Kennung',
                  cell: (installation) => installation.installationNumber,
                },
                {
                  id: 'type',
                  header: 'Typ',
                  cell: (installation) => installation.installationType,
                },
              ]}
            />
            <MaintenanceListFooter
              page={installations.data.page}
              pageSize={installations.data.pageSize}
              total={installations.data.total}
              onPageChange={(page) =>
                setSearch(maintenanceProjectInstallationsPath(projectId, { page }))
              }
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
